import { Box, Flex, Heading } from "@givenwell/components";
import { colors, fonts } from "@givenwell/theme";
import { ReactNode, useCallback, useState } from "react";
import { SidebarDialogButton } from "../layout/SidebarDialogButton";

type PageWrapperProps = {
  children?: ReactNode;
  title?: ReactNode;
  subTitle?: ReactNode;
  rightContent?: ReactNode;
  footer?: ReactNode;
  maxWidth?: 600 | 1400;
};
export function PageWrapper({ title, subTitle, children, rightContent, footer, maxWidth }: PageWrapperProps) {
  const showTopBar = !!title || !!subTitle || !!rightContent;

  // Tracks whether the end of the scrollable content is in view, so the footer can cast a
  // shadow over any content still hidden below it.
  const [scrolledToBottom, setScrolledToBottom] = useState(true);
  const sentinelRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries.at(-1);
        if (entry) {
          setScrolledToBottom(entry.isIntersecting);
        }
      },
      { root: el.parentElement, rootMargin: "0px 0px 2px 0px", threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      css={{
        backgroundColor: colors.cream,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        overflowY: "auto",
        flex: "1 0 0px",
        borderRadius: 0,
        boxShadow: "0 10px 40px rgba(0,0,0,0.05), 0 0 0 0.5px rgba(0,0,0,0.05)",
        "@md": {
          borderRadius: 18,
        },
      }}
    >
      <WidthWrapper maxWidth={maxWidth}>
        <Flex
          css={{
            flexDirection: "column",
            w: "100%",
            alignItems: "stretch",
            flex: "1 0 0px",
            px: 16,
            pb: footer ? 0 : "max(var(--safe-area-inset-bottom), 16px)",
          }}
        >
          {showTopBar && (
            <Flex
              css={{
                gap: 8,
                w: "100%",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                py: 16,
                minHeight: 72,
              }}
            >
              <Flex css={{ alignItems: "center" }}>
                <SidebarDialogButton />
                <Flex
                  css={{
                    flexDirection: "column",
                  }}
                >
                  <Heading
                    css={{
                      fontScale: "2xl",
                      weight: 400,
                      lineHeight: 1,
                      align: "left",
                      fontFamily: fonts.ftHabit,
                    }}
                  >
                    {title}
                  </Heading>
                  {subTitle && (
                    <Heading
                      css={{
                        fontScale: "lg",
                        weight: "400",
                        mt: 8,
                        color: colors.text2,
                      }}
                    >
                      {subTitle}
                    </Heading>
                  )}
                </Flex>
              </Flex>
              <Box css={{ flex: "1 0 0px" }} />
              {rightContent}
            </Flex>
          )}
          {children}
        </Flex>
      </WidthWrapper>
      {footer && <Box ref={sentinelRef} css={{ height: 1, flex: "0 0 auto", pointerEvents: "none" }} />}
      {footer && (
        <Box
          css={{
            position: "sticky",
            bottom: 0,
            flex: "0 0 auto",
            display: "flex",
            flexDirection: "column",
            backgroundColor: colors.cream,
            boxShadow: scrolledToBottom
              ? "0 -0.5px 0px rgba(0,0,0,0), 0 0 40px rgba(0,0,0,0)"
              : "0 -0.5px 0px rgba(0,0,0,0.1), 0 0 40px rgba(0,0,0,0.05)",
            transition: "box-shadow 100ms ease",
          }}
        >
          <WidthWrapper maxWidth={maxWidth} grow={false}>
            <Flex
              css={{
                flexDirection: "column",
                w: "100%",
                alignItems: "stretch",
                px: 16,
                pt: 16,
                pb: "max(var(--safe-area-inset-bottom), 16px)",
              }}
            >
              {footer}
            </Flex>
          </WidthWrapper>
        </Box>
      )}
    </Box>
  );
}

function WidthWrapper({
  maxWidth,
  children,
  grow = true,
}: {
  maxWidth?: number;
  children?: ReactNode;
  grow?: boolean;
}) {
  if (!maxWidth) {
    return <>{children}</>;
  }
  return (
    <Box
      css={{
        width: "100%",
        maxWidth: maxWidth,
        mx: "auto",
        flex: grow ? "1 0 0px" : "0 0 auto",
        display: "flex",
        flexDirection: "column",
        [`@media (min-width: ${maxWidth + 200 * 3}px)`]: { transform: "translateX(-100px)" },
      }}
    >
      {children}
    </Box>
  );
}

type SplitPageWrapperProps = {
  sidebar?: ReactNode;
  content?: ReactNode;
  /**
   * Which split should be prioritized for small viewports
   */
  defaultSplit: "sidebar" | "content";
};
export function SplitPageWrapper({ sidebar, content, defaultSplit }: SplitPageWrapperProps) {
  return (
    <Box
      data-default-split={defaultSplit}
      css={{
        backgroundColor: colors.cream,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "row",
        flex: "1 0 0px",
        borderRadius: 0,
        boxShadow: "0 10px 40px rgba(0,0,0,0.05), 0 0 0 0.5px rgba(0,0,0,0.05)",
        overflow: "hidden",
        "@md": {
          borderRadius: 18,
        },
      }}
    >
      {sidebar}
      {content}
    </Box>
  );
}

export type SplitPageSidebarProps = {
  children?: ReactNode;
  title?: ReactNode;
};
export function SplitPageSidebar({ children, title }: SplitPageSidebarProps) {
  return (
    <Box
      css={{
        flexDirection: "column",
        width: "100%",
        alignItems: "stretch",
        flex: "2 0 0px",

        display: "none",
        "[data-default-split=sidebar] &": { display: "flex" },
        overflowX: "hidden",
        "@md": {
          display: "flex",
        },
        "@lg": {
          flex: "1 0 0px",
        },
      }}
    >
      <Flex
        css={{
          gap: 8,
          w: "100%",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          py: 16,
          minHeight: 72,
          px: 16,
        }}
      >
        <Flex css={{ alignItems: "center" }}>
          <SidebarDialogButton />
          <Flex
            css={{
              flexDirection: "column",
            }}
          >
            <Heading
              css={{
                fontScale: "2xl",
                weight: 400,
                lineHeight: 1,
                align: "left",
                fontFamily: fonts.ftHabit,
              }}
            >
              {title}
            </Heading>
          </Flex>
        </Flex>
      </Flex>
      <Flex
        css={{
          flexDirection: "column",
          alignItems: "stretch",
          flex: "1 0 0px",
          overflowY: "auto",
          px: 16,
          pb: "max(var(--safe-area-inset-bottom), 16px)",
        }}
      >
        {children}
      </Flex>
    </Box>
  );
}

export type SplitPageContentProps = {
  children?: ReactNode;
  title?: ReactNode;
};
export function SplitPageContent({ children, title }: SplitPageContentProps) {
  return (
    <Box
      css={{
        flexDirection: "column",
        w: "100%",
        alignItems: "stretch",
        px: 16,
        pb: "max(var(--safe-area-inset-bottom), 16px)",
        borderLeft: `1px solid ${colors.outline2}`,
        overflowY: "auto",

        flex: "3 0 0px",
        display: "none",
        overflowX: "hidden",
        "[data-default-split=content] &": { display: "flex" },
        "@md": {
          display: "flex",
        },
        "@lg": {
          flex: "2 0 0px",
        },
        "@xl": {
          flex: "3 0 0px",
        },
      }}
    >
      <Flex
        css={{
          gap: 8,
          w: "100%",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          py: 16,
          minHeight: 72,
        }}
      >
        <Flex css={{ alignItems: "center" }}>
          <SidebarDialogButton />
          <Flex
            css={{
              flexDirection: "column",
            }}
          >
            <Heading
              css={{
                fontScale: "2xl",
                weight: 400,
                lineHeight: 1,
                align: "left",
                fontFamily: fonts.ftHabit,
              }}
            >
              {title}
            </Heading>
          </Flex>
        </Flex>
      </Flex>
      {children}
    </Box>
  );
}
